"use client";

import React from "react";
import { Link as LinkIcon, Github, Mail, Sparkles, Files, Users, ClipboardList, BookMarked, BrainCircuit, ScanSearch, FileText } from "lucide-react";

/* ----------------------------- Hero ----------------------------- */
const HeroContent = () => (
  <div className="md:w-1/2 mb-10 md:mb-0">
    <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 leading-tight mb-4">
      <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-500 to-orange-500">
        Build Better Futures,
      </span>{" "}
      <br className="hidden md:block" />
      One Letter at a Time
    </h2>
    <p className="text-lg sm:text-xl text-gray-700 mb-8 max-w-md mx-auto md:mx-0">
      Transform the way you write letters of recommendation with AI-powered tools 
      that help your students unlock their potential and achieve their dreams.
    </p>
    <div className="text-center md:text-left mb-6">
      <span className="inline-block px-4 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full text-sm font-semibold shadow-md">
        ✨ Craft Letters That Open Doors
      </span>
    </div>
  </div>
);

const EmailForm = ({ email, onEmailChange }) => (
  <form
    action="/subscribe"
    className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
  >
    <input
      type="email"
      placeholder="Enter your email"
      value={email}
      onChange={onEmailChange}
      required
      className="px-6 py-3 rounded-full border border-gray-300 focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none text-slate-800 w-full sm:w-auto transition"
    />
    <button
      type="submit"
      className="px-6 py-3 bg-gradient-to-r from-teal-600 to-orange-600 text-white rounded-full font-semibold shadow-md hover:from-teal-700 hover:to-orange-700 transition"
    >
      Start Crafting LORs
    </button>
  </form>
);

const HeroSection = ({
  email,
  onEmailChange,
  heroImageSrc = "/rec-5.png",
  heroImageAlt = "AI assisting an educator in writing a professional letter of recommendation",
}) => (
  <main className="w-full max-w-7xl flex flex-col md:flex-row items-center justify-between py-12 md:py-20 px-4 text-center md:text-left">
    <HeroContent />
    <div className="md:w-1/2">
      {/* Image container */}
      <div className="relative w-full h-64 sm:h-80 md:h-96 rounded-3xl overflow-hidden shadow-lg">
        <img
          src={heroImageSrc}
          alt={heroImageAlt}
          className="object-cover w-full h-full"
          onError={(e) => {
            e.target.src = 'https://placehold.co/400x320/9c27b0/ffffff?text=Rec-Builder';
          }}
        />
      </div>
    </div>
  </main>
);

/* --------------------------- Features --------------------------- */
const FeatureCard = ({ title, description, icon }) => (
  <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-lg transform transition-all hover:scale-[1.02] hover:shadow-2xl">
    <div className="text-4xl text-teal-600 mb-4">{icon}</div>
    <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-600">{description}</p>
  </div>
);

const FeaturesContent = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <FeatureCard
        title="AI-Powered Content Generation"
        description="Generate drafts instantly based on student information and your professional input."
        icon={<BrainCircuit size={48} />}
      />
      <FeatureCard
        title="Customizable Templates"
        description="Choose from a library of professional templates tailored for different college programs and scholarships."
        icon={<BookMarked size={48} />}
      />
      <FeatureCard
        title="Student Profile Integration"
        description="Import student achievements, activities, and goals to create a truly personalized LOR."
        icon={<Users size={48} />}
      />
      <FeatureCard
        title="ATS-Friendly Formatting"
        description="Ensure your letters are easily parsed and read by Applicant Tracking Systems for maximum visibility."
        icon={<ClipboardList size={48} />}
      />
      <FeatureCard
        title="ATS-Friendly Resume Scan"
        description="Quickly upload a student's resume for an automated scan to highlight keywords and accomplishments that will resonate with admissions officers."
        icon={<ScanSearch size={48} />}
      />
      <FeatureCard
        title="Document Management"
        description="Securely store and manage all of your recommendation letters and student profiles in one organized place."
        icon={<FileText size={48} />}
      />
    </div>
  );
};

const FeaturesSection = () => (
  <section id="features" className="w-full max-w-7xl py-12 px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-12">
      <h3 className="text-3xl font-bold text-slate-900 mb-4">
        Key Features for Educators
      </h3>
      <p className="text-lg text-slate-600">
        Craft compelling letters with a fraction of the effort.
      </p>
    </div>
    <FeaturesContent />
  </section>
);

/* ----------------------------- CTA ------------------------------ */
const CallToAction = () => (
  <section className="w-full max-w-7xl py-12 px-4 text-center">
    <div className="bg-gradient-to-r from-orange-600 to-red-700 text-white rounded-3xl p-8 sm:p-12 md:p-16 shadow-xl">
      <h3 className="text-3xl sm:text-4xl font-bold mb-4">
        Ready to Empower Your Students?
      </h3>
      <p className="text-lg mb-8">
        Streamline your workflow and provide students with the powerful letters of
        recommendation they deserve.
      </p>
      <a
        href="/subscribe"
        className="px-8 py-4 bg-white text-orange-700 font-bold rounded-full text-lg shadow-md hover:bg-slate-100 transition transform hover:scale-105"
      >
        Start Today!
      </a>
    </div>
  </section>
);

/* ---------------------------- Footer ---------------------------- */
const Footer = () => (
  <footer className="w-full max-w-7xl flex flex-col items-center py-8 px-4 border-t border-gray-200 mt-12">
    <div className="flex space-x-6 text-gray-500 mb-4">
      <a href="#" className="hover:text-teal-600 transition" aria-label="Link">
        <LinkIcon size={24} />
      </a>
      <a href="#" className="hover:text-teal-600 transition" aria-label="GitHub">
        <Github size={24} />
      </a>
      <a href="#" className="hover:text-teal-600 transition" aria-label="Email">
        <Mail size={24} />
      </a>
    </div>
    <p className="text-gray-500 text-sm">
      &copy; {new Date().getFullYear()} LoR App. All rights reserved.
    </p>
  </footer>
);

/* ----------------------------- Page ----------------------------- */
const Features = () => {
  const [email, setEmail] = React.useState("");

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-start p-4 sm:p-8 font-sans">
      <HeroSection
        email={email}
        onEmailChange={(e) => setEmail(e.target.value)}
        heroImageSrc="/rec-5.png"
        heroImageAlt="AI assisting an educator in writing a professional letter of recommendation"
      />
      <FeaturesSection />
      <CallToAction />
      <Footer />
    </div>
  );
};

export default Features;